/**
 * @module Experience/repdash
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
// core
import { Component, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { Subject } from 'rxjs';
// interface
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
// constants
import { ACCOUNT_LANDING_CONSTANTS } from '@experience/landing/common/constants/account-landing.constants';
import { SYMBOL_CONSTANTS } from '@support/shared/constants/device.constants';
import { UsageBucketModel } from '@experience/landing/common/models/usage-bucket.model';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CustomerUsageSummaryResultsLineUsage } from '@support/api/proxies/responses/api-2009.interface';
import { takeUntil } from 'rxjs/operators';
import { UserService } from '@support/shared/services/user.service';
import { CustomerAccountLineBillingDetails } from '@support/api/proxies/responses/api-2040.interface';
import moment from 'moment';
import {
  CustomerUsageSummaryResultsLines,
  CustomerUsageSummaryResults,
} from '@support/api/proxies/responses/api-2009.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { APIError } from '@support/api/common/error.interface';

/**
 * Component to host the Usage-Detail in Account-Landing
 */
@Component({
  selector: 'tmo-p10m-usage-detail',
  templateUrl: './p10m-usage-detail.component.html',
  styleUrls: ['../../../../../../style-sheets/components/html-progressbar.component.scss'],
})
export class P10mUsageDetailComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Store symbol constant value
   */
  public symbolConstants: ObjectInterface;

  /**
   * @type {ConfigData}
   * @memberof UsageDetailComponent
   */
  @Input() public contentData: ConfigData;

  /**
   * Subject to manage subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  /**
   * stores primary msisdn
   */
  @Input() public primaryMsisdn: string;

  /**
   * stores usage model
   */
  public usageBucketModel: UsageBucketModel;

  /**
   * to show fraud Indicator
   */
  public showIndicator: boolean;

  /**
   * to show unlimited string
   */
  public unlimitedData: string;
  /**
   * to store the annual datapass
   */
  public annualDataPass:
    | CustomerUsageSummaryResultsLineUsage
    | CustomerUsageSummaryResultsLineUsage[];
  /**
   * To show start date
   */
  public startDate: string;

  /**
   * to show end date
   */
  public endDate: string;

  /**
   * to store the annual datapass
   */
  public hideSpinner: boolean;

  /**
   * to store the annual datapass
   */
  public activeDataPasses: CustomerUsageSummaryResultsLineUsage[];
  /**
   * Creates an instance of UsageDetailComponent.
   */
  constructor(
    private stateStoreUtilService: StateStoreUtilsService,
    private userService: UserService,
    private objectService: ObjectService,
    private commonUtil: CommonUtilService
  ) {
    this.unlimitedData = ACCOUNT_LANDING_CONSTANTS.UNLIMITED;
    this.symbolConstants = SYMBOL_CONSTANTS;
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Initializes the data for usage detail component
   * Sets billingType and remainingDays
   * @memberof UsageDetailComponent
   */
  public ngOnInit(): void {}

  public ngOnChanges(): void {
    this.getLineUsageDetails();
  }

  public setDataPassValues(): void {
    const dataPasses:
      | CustomerUsageSummaryResultsLineUsage
      | CustomerUsageSummaryResultsLineUsage[] = this.usageBucketModel.usageBucket.bucket.dataPass;
    if (Array.isArray(dataPasses) && dataPasses.length) {
      const dupDataPasses = [...dataPasses];
      const annualDataPassIndex = dupDataPasses.findIndex(
        (dataPass: CustomerUsageSummaryResultsLineUsage) =>
          dataPass.usageSubType.toLowerCase() === ACCOUNT_LANDING_CONSTANTS.ANNUAL_DATA_PASS
      );
      this.annualDataPass = dupDataPasses.splice(annualDataPassIndex, 1)[0];
      this.activeDataPasses = dupDataPasses.filter(
        (activeDataPass: CustomerUsageSummaryResultsLineUsage) =>
          activeDataPass.usageSubType.toLowerCase() === ACCOUNT_LANDING_CONSTANTS.SESSION_PASS_DATA
      );
    } else {
      this.annualDataPass = dataPasses;
    }
    this.userService
      .getCustomerBillingDetailsData(this.stateStoreUtilService.getLineId())
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (customerAccountLineBillingDetails: CustomerAccountLineBillingDetails) => {
          this.startDate = moment(
            this.objectService.get(
              customerAccountLineBillingDetails,
              'lineDetails.activationDate',
              ''
            ),
            'MM-DD-YYYY'
          ).format('MM/DD/YYYY');
          this.endDate = moment(this.startDate)
            .add(1, 'year')
            .subtract(1, 'day')
            .format('MM/DD/YYYY');
          this.hideSpinner = true;
        },
        (error: APIError[]) => {
          this.hideSpinner = true;
        }
      );
  }

  /**
   * Unsubscribes for all subscriptions
   * @memberof UsageDetailComponent
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getLineUsageDetails(): void {
    this.hideSpinner = false;
    this.userService
      .getUsageDetails(this.primaryMsisdn)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (usageSummary: CustomerUsageSummaryResults) => {
          let lines: CustomerUsageSummaryResultsLines[] = this.objectService.get(
            usageSummary,
            'lines'
          );
          if (this.commonUtil.isNotEmpty(lines)) {
            lines = lines.filter(
              (line: CustomerUsageSummaryResultsLines) => line.msisdn === this.primaryMsisdn
            );
            if (this.commonUtil.isNotEmpty(lines)) {
              const lineUsageSummary = this.objectService.get(lines, '0');
              this.usageBucketModel = new UsageBucketModel(
                lineUsageSummary,
                false,
                this.objectService
              );
              this.usageBucketModel ? this.setDataPassValues() : '';
            }
          }
        },
        (error: APIError[]) => {}
      );
  }
}
